// An override rule's conditions as one line.
//
// The editor gives every rule a full width form of selects, which is right for
// editing one and wrong for reading nine. A rule's conditions are always joined
// with AND (`convertToCaseWhenStatement` does exactly that), so a collapsed row
// can state the whole test without inventing any logic the builder cannot
// express.
//
// The verb is printed as the builder offers it. There are fifteen of them and
// they are already written as English, so a second vocabulary here would put
// words on screen that match nothing in the selects.
import { Rule } from '../../../models/rules';
import { Condition, getVerbSettings } from '../../../models/sqlform';
import { computationDisplayName } from '../../../utils';

/** Past this, values are summarised rather than listed. */
const MAX_VALUES = 2;

/**
 * The values, in the shape the verb takes them.
 *
 * The fifteen verbs produce four shapes and a single `join(', ')` is wrong for
 * two of them: `is between` takes exactly two bounds and reads as a range
 * rather than a list, while `is any of` and `is none of` take up to
 * ninety-nine and would otherwise render a row wider than the card. `is known`
 * and `is unknown` take none at all, which is why this can return the empty
 * string and the caller filters it out rather than leaving a trailing space.
 */
function valuesOf(condition: Condition): string {
  const values = (condition?.complements ?? []).filter(
    (value) => value !== null && value !== undefined && String(value).trim()
  );
  if (!values.length) return '';

  // Keyed on what the verb accepts, not on how many values happen to be
  // present, so a half-typed `is between` still reads as a list rather than
  // claiming a range it does not have.
  const max = getVerbSettings(condition.verb)?.characteristics?.max ?? 1;
  if (max === 2 && values.length === 2) return `${values[0]} and ${values[1]}`;

  if (values.length <= MAX_VALUES) return values.join(', ');
  const shown = values.slice(0, MAX_VALUES).join(', ');
  return `${shown} and ${values.length - MAX_VALUES} more`;
}

/** One condition, as `field verb values`. */
export function overrideConditionSentence(condition: Condition): string {
  const subject = condition?.subject?.subject;
  const field = subject ? computationDisplayName(subject) : '';
  return [field, condition?.verb ?? '', valuesOf(condition)]
    .filter(Boolean)
    .join(' ');
}

/**
 * The whole test, or the raw SQL for a rule written in SQL.
 *
 * An advanced rule is shown as what it is. Rendering it as a sentence would
 * mean parsing SQL to say it back in words, and a paraphrase that drifts from
 * the thing being executed is worse than the thing itself.
 */
export function overrideRuleSentence(rule: Rule): string {
  if (rule?.ruleType === 'advanced') return (rule.sqlConditions ?? '').trim();
  const conditions = rule?.formConditions ?? [];
  return conditions
    .map(overrideConditionSentence)
    .filter(Boolean)
    .join(' and ');
}
